import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';
import { app } from '../config/firebase';
import { toDate } from './booking';

/**
 * Booking creation, server-side.
 *
 * This app used to write the booking document itself. It computed the price
 * honestly — but nothing forced a client to be this client, and the rules
 * validated no money at all, so anyone could book a $600 lodge for $1.
 * Availability and capacity were unenforceable for a related reason: deciding
 * whether a date is free means reading every other booking for the listing,
 * and firestore.rules cannot query.
 *
 * So `createBooking` in vhakacha-functions is now the only door, and
 * `bookings` is `allow create: if false` for every client.
 *
 * What we send is INTENT — which listing, which dates, how many people. Never
 * a price. The server reads the listing, computes the money, checks
 * availability inside a transaction, and writes.
 */

/** Must match REGION in vhakacha-functions/src/lib/config.ts. */
const REGION = 'europe-west1';

const functions = getFunctions(app, REGION);

// Mirrors the provider console's switch. Off unless explicitly enabled, so a
// normal build talks to the real project exactly as before.
if (process.env.REACT_APP_FIREBASE_USE_EMULATOR === 'true') {
  connectFunctionsEmulator(
    functions,
    process.env.REACT_APP_FIREBASE_EMULATOR_HOST || '127.0.0.1',
    Number(process.env.REACT_APP_FIREBASE_FUNCTIONS_EMULATOR_PORT || 5001)
  );
}

const callCreateBooking = httpsCallable(functions, 'createBooking');

/**
 * Reuses toDate() from utils/booking rather than `new Date(value)`.
 *
 * The date pickers use AdapterDayjs, so a chosen range holds dayjs objects,
 * not Dates — and elsewhere these values arrive as Firestore Timestamps or
 * the legacy "DD/MM/YYYY, HH:mm:ss" string. toDate() already handles all of
 * them; it is what buildBooking used, and re-implementing a narrower version
 * here would silently drop whichever shape it missed.
 */
const toIso = (value) => {
  const date = toDate(value);
  return date ? date.toISOString() : undefined;
};

/**
 * @param {object} params
 * @param {object} params.listing    a document from the `listings` collection
 * @param {object} params.travelers  { adults, children, infants }
 * @param {Array}  params.dateRange  [start, end]
 * @returns {Promise<{bookingId: string, totals: object}>}
 */
export async function createBookingOnServer({
  listing,
  travelers,
  dateRange = [],
  info = '',
  user,
  itineraryId = null,
}) {
  const [start, end] = Array.isArray(dateRange) ? dateRange : [];

  try {
    const { data } = await callCreateBooking({
      listingId: listing?.id,
      startDate: toIso(start),
      endDate: toIso(end),
      travelers,
      info,
      itineraryId,
      customerName: user?.displayName || '',
      customerContact: user?.email || '',
      // Deliberately no price fields. They would be ignored, and sending them
      // would imply to the next reader that they mattered.
    });
    return data;
  } catch (error) {
    throw new Error(describeBookingError(error));
  }
}

/**
 * The server's refusals are the useful part — "the 3rd is already booked",
 * "the maximum is 6". Those are specific and actionable, so they are passed
 * through rather than replaced with a house string.
 */
export function describeBookingError(error) {
  const code = error?.code || '';
  const message = error?.message || '';

  switch (code) {
    case 'functions/failed-precondition':
      // Availability, capacity, unpublished listing. Always says which.
      return message || 'Those dates are not available.';
    case 'functions/unauthenticated':
      return 'Please sign in to make a booking.';
    case 'functions/not-found':
      return 'That listing is no longer available.';
    case 'functions/invalid-argument':
      return message || 'Please check the dates and try again.';
    case 'functions/internal':
    case 'functions/unavailable':
      return 'Could not reach the server. Check your connection and try again.';
    default:
      console.error('Unexpected error from createBooking:', error);
      return 'Could not complete the booking. Please try again.';
  }
}
